import { Injectable, inject, signal, computed, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { ApiService } from './api.service';
import { ToastService } from './toast.service';
import { ELocalStorage } from '../enums/ELocalStorage';
import { EModelApiPaths } from '../enums/EModelApiPaths';
import { User, LoginRequest, RegisterRequest } from '../../interfaces/user.interface';

interface BackendAuthResponse {
  success?: boolean;
  data?: {
    message?: string;
    tokenResp?: { token: string }[];
    user?: User;
  };
  errors?: { message: string }[];
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private api = inject(ApiService);
  private router = inject(Router);
  private toast = inject(ToastService);
  private platformId = inject(PLATFORM_ID);

  user = signal<User | null>(null);
  // AM admin token — app-level. Fetched via /schema/{userPath}/token on app
  // bootstrap and survives logout. Sent in `x-am-authorization` header.
  token = signal<string | null>(null);
  // AM_DB user token — user-level. Issued by /website-login on successful auth.
  // Sent in `x-am-user-authorization` header. Cleared on logout.
  userToken = signal<string | null>(null);
  isLoggedIn = computed(() => !!this.user() && !!this.userToken());
  openLoginModal = signal(false);
  loginLoading = signal(false);
  registerLoading = signal(false);
  pendingRedirectUrl = signal<string | null>(null);

  /**
   * Bootstraps the auth state. Called from APP_INITIALIZER.
   *  1. Restores the app-level AM admin token from localStorage if present, or
   *     fetches a fresh one from the BE.
   *  2. Restores the per-user AM_DB token + user object from localStorage if
   *     a previous session exists.
   */
  async init(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) return;

    // Restore the app-level AM token from localStorage if available — every
    // subsequent API call needs it as `x-am-authorization`.
    const storedAmToken = localStorage.getItem(ELocalStorage.AM_AUTH);
    if (storedAmToken && storedAmToken !== 'undefined') {
      this.token.set(storedAmToken);
    }

    // Restore the user session if present.
    const storedUserToken = localStorage.getItem(ELocalStorage.DEFAULT_AM_AUTH);
    const storedUser = localStorage.getItem(ELocalStorage.USER);
    const validUserSession =
      storedUserToken && storedUserToken !== 'undefined' &&
      storedUser && storedUser !== 'undefined';
    if (validUserSession) {
      try {
        this.userToken.set(storedUserToken);
        this.user.set(JSON.parse(storedUser!));
      } catch {
        this.clearUserAuth();
      }
    } else {
      this.clearUserAuth();
    }

    // Ensure we end up with a usable AM token (fetches if cache was empty).
    await this.ensureAMToken();
  }

  /**
   * Returns a valid AM admin token. Order of preference:
   *   1. In-memory signal
   *   2. localStorage
   *   3. Fetch a fresh one from `/system-api/{userPath}/token`
   * The result is mirrored to both signal and localStorage so subsequent calls
   * are synchronous.
   */
  async ensureAMToken(): Promise<string | null> {
    if (!isPlatformBrowser(this.platformId)) return null;

    const inMemory = this.token();
    if (inMemory) return inMemory;

    const stored = localStorage.getItem(ELocalStorage.AM_AUTH);
    if (stored && stored !== 'undefined') {
      this.token.set(stored);
      return stored;
    }

    const fresh = await this.api.getAMToken();
    if (fresh) {
      this.token.set(fresh);
      localStorage.setItem(ELocalStorage.AM_AUTH, fresh);
    }
    return fresh;
  }

  async login(credentials: LoginRequest, onSuccess?: () => void): Promise<void> {
    this.loginLoading.set(true);

    // Always fetch a fresh AM admin token before the login call so that
    // x-am-authorization is guaranteed to be valid (matches the lms_webapp_fe
    // login() pattern: getAMToken() → set header → call login API).
    const amToken = await this.api.getAMToken();
    if (!amToken) {
      this.loginLoading.set(false);
      // Network/connectivity failure to system token endpoint — error
      // interceptor will already have toasted the underlying problem.
      return;
    }
    this.token.set(amToken);
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(ELocalStorage.AM_AUTH, amToken);
    }

    this.api.callCustomApi<BackendAuthResponse>(EModelApiPaths.LOGIN, 'POST', credentials).subscribe({
      next: (res) => {
        this.loginLoading.set(false);
        const tokens = res?.data?.tokenResp || [];
        // The BE issues only the per-user AM_DB token at index 0 — the FE
        // manages its own AM admin token separately via getAMToken().
        const dbUserToken = tokens[0]?.token || '';
        if (dbUserToken && res.data?.user) {
          this.setUserAuth(dbUserToken, res.data.user);
          this.toast.success('Welcome back, ' + res.data.user.name + '!');
          this.openLoginModal.set(false);
          const loginRedirect = this.pendingRedirectUrl();
          if (loginRedirect) { this.pendingRedirectUrl.set(null); this.router.navigateByUrl(loginRedirect); }
          if (onSuccess) onSuccess();
        }
        // No else: if the response is malformed enough to lack a token, the
        // global error interceptor will surface a meaningful message — no
        // need for a duplicate generic "Login failed" toast here.
      },
      error: () => {
        // Error already toasted by the global error interceptor — just clear
        // the loading state.
        this.loginLoading.set(false);
      },
    });
  }

  async register(data: RegisterRequest, onSuccess?: () => void): Promise<void> {
    this.registerLoading.set(true);

    // Same pattern as login() — fetch a fresh AM token before the call.
    const amToken = await this.api.getAMToken();
    if (!amToken) {
      this.registerLoading.set(false);
      return;
    }
    this.token.set(amToken);
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(ELocalStorage.AM_AUTH, amToken);
    }

    this.api.callCustomApi<BackendAuthResponse>(EModelApiPaths.REGISTER, 'POST', data).subscribe({
      next: (res) => {
        this.registerLoading.set(false);
        const tokens = res?.data?.tokenResp || [];
        const dbUserToken = tokens[0]?.token || '';
        if (dbUserToken && res.data?.user) {
          this.setUserAuth(dbUserToken, res.data.user);
          this.toast.success('Account created successfully!');
          this.openLoginModal.set(false);
          const registerRedirect = this.pendingRedirectUrl();
          if (registerRedirect) { this.pendingRedirectUrl.set(null); this.router.navigateByUrl(registerRedirect); }
          if (onSuccess) onSuccess();
        }
      },
      error: () => {
        this.registerLoading.set(false);
      },
    });
  }

  private extractError(res: any): string {
    if (!res) return '';
    if (res?.errors?.length) return res.errors[0].message || res.errors[0].code || '';
    if (typeof res?.message === 'string') return res.message;
    return '';
  }

  logout(): void {
    this.clearUserAuth();
    this.router.navigate(['/home']);
  }

  private setUserAuth(dbUserToken: string, user: User): void {
    this.userToken.set(dbUserToken);
    this.user.set(user);

    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(ELocalStorage.DEFAULT_AM_AUTH, dbUserToken);
      localStorage.setItem(ELocalStorage.USER, JSON.stringify(user));
    }
  }

  updateUser(patch: Partial<User>): void {
    const cur = this.user();
    if (!cur) return;
    const next = { ...cur, ...patch } as User;
    this.user.set(next);
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(ELocalStorage.USER, JSON.stringify(next));
    }
  }

  /**
   * Drops the cached AM admin token from both memory and localStorage. The next
   * call to `ensureAMToken()` will fetch a fresh one from the BE. Used by the
   * auth interceptor when the BE rejects the current AM token as stale.
   */
  clearAmToken(): void {
    this.token.set(null);
    if (isPlatformBrowser(this.platformId)) {
      localStorage.removeItem(ELocalStorage.AM_AUTH);
    }
  }

  /**
   * Clears the per-user session (AM_DB token + user). The app-level AM admin
   * token stays so the next request can still hit public/anonymous endpoints.
   */
  private clearUserAuth(): void {
    this.userToken.set(null);
    this.user.set(null);

    if (isPlatformBrowser(this.platformId)) {
      localStorage.removeItem(ELocalStorage.DEFAULT_AM_AUTH);
      localStorage.removeItem(ELocalStorage.USER);
    }
  }

  /**
   * Returns the AM admin token synchronously. Used by the auth interceptor to
   * attach `x-am-authorization` to outgoing requests. Falls back to localStorage
   * so we still send a token if a request fires before init() has populated the
   * in-memory signal (e.g. during hydration race conditions).
   */
  getToken(): string | null {
    const inMemory = this.token();
    if (inMemory) return inMemory;
    if (isPlatformBrowser(this.platformId)) {
      const stored = localStorage.getItem(ELocalStorage.AM_AUTH);
      if (stored && stored !== 'undefined') {
        // Lazily restore the signal so future reads are O(1).
        this.token.set(stored);
        return stored;
      }
    }
    return null;
  }

  /**
   * Returns the per-user AM_DB token synchronously, with the same localStorage
   * fallback as getToken() for the same reason.
   */
  getUserToken(): string | null {
    const inMemory = this.userToken();
    if (inMemory) return inMemory;
    if (isPlatformBrowser(this.platformId)) {
      const stored = localStorage.getItem(ELocalStorage.DEFAULT_AM_AUTH);
      if (stored && stored !== 'undefined') {
        this.userToken.set(stored);
        return stored;
      }
    }
    return null;
  }

  showLogin(redirectTo?: string): void {
    if (redirectTo) this.pendingRedirectUrl.set(redirectTo);
    this.openLoginModal.set(true);
  }

  sessionExpired(): void {
    const currentUrl = this.router.url;
    this.clearUserAuth();
    setTimeout(() => {
      if (currentUrl && currentUrl !== '/' && !currentUrl.startsWith('/home')) {
        this.pendingRedirectUrl.set(currentUrl);
      }
      this.openLoginModal.set(true);
    }, 0);
  }
}
